import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { markSiteUnsynced } from "../../../utils/syncStore";

export interface FixCamerasResult {
  open: boolean;
  camerasChanged: any[];
  uidsFixed: number;
  // Integration-specific updates reported as messages, e.g. Milestone's
  // "Updated recording server ... for ..." when a camera moved servers.
  updateMessages: string[];
  error: boolean;
  errorMessage?: string;
}

export const EMPTY_FIX_RESULT: FixCamerasResult = {
  open: false,
  camerasChanged: [],
  uidsFixed: 0,
  updateMessages: [],
  error: false,
};

/** True when a row's deployed camera sits on a different recording server
 * than the NVR now reports (the "Old server" hint in AutoAddTable). */
export const hasMovedRecordingServer = (row: any): boolean =>
  !!row?.camera_current_recording_server &&
  !!row?.recorder_name &&
  row.camera_current_recording_server.toUpperCase() !==
    row.recorder_name.toUpperCase();

/** A 400 from autofix carries a user-facing reason (e.g. a stale camera list). */
const errorMessageFrom = (reason: AxiosError): string | undefined => {
  if (reason.response?.status !== 400) return undefined;
  const data: any = reason.response?.data;
  if (Array.isArray(data) && typeof data[0] === "string") return data[0];
  if (typeof data?.detail === "string") return data.detail;
  return undefined;
};

/**
 * Brings the site's deployed cameras in line with the refreshed NVR camera list.
 *
 * Mirrors what V1's auto-add submit does for existing cameras (ENG-2692): the
 * backend runs the integration's update_existing() — device UIDs, and for
 * Milestone the recording server a camera moved to — then the name fix that
 * "Fix Camera Names" used to do on its own. The camera records (id, products,
 * config) are kept.
 *
 * POST nvr_camera/{siteId}/autofix/ -> names_fixed, uids_fixed, messages, ...
 * The backend refuses (400) when the camera list isn't fresh; refreshing first
 * clears that.
 */
export const handleFixCameras = (
  setFixingCameras: (value: boolean) => void,
  setFixResult: (value: FixCamerasResult) => void,
  refreshCameraList: () => void,
  navigate: NavigateFunction
) => {
  const AxiosUI = createApi(``);
  const siteId = localStorage.siteId;

  setFixingCameras(true);

  const retryFunction = () => {
    handleFixCameras(
      setFixingCameras,
      setFixResult,
      refreshCameraList,
      navigate
    );
  };

  AxiosUI.post(`nvr_camera/${siteId}/autofix/`).then(
    (response: AxiosResponse) => {
      const data = response.data || {};
      const camerasChanged = Array.isArray(data.names_fixed)
        ? data.names_fixed
        : [];
      const uidsFixed = Array.isArray(data.uids_fixed)
        ? data.uids_fixed.length
        : 0;
      const updateMessages: string[] = (
        Array.isArray(data.messages) ? data.messages : []
      )
        .map((m: any) => m?.message)
        .filter(
          (text: unknown): text is string =>
            typeof text === "string" && !text.startsWith("Updated camera_uid")
        );
      setFixingCameras(false);
      setFixResult({
        open: true,
        camerasChanged,
        uidsFixed,
        updateMessages,
        error: false,
      });
      if (camerasChanged.length > 0 || uidsFixed > 0 || updateMessages.length) {
        // These change connector settings — flag the site unsynced so the
        // Sync controls/banner light up. The auto-add route
        // (/sites/autoaddcamera) doesn't carry the site id, so pass it.
        markSiteUnsynced(siteId);
        // Re-fetch the NVR list so the deployed names and servers update and
        // the old -> new hints clear for the cameras that were fixed.
        refreshCameraList();
      }
    },
    (reason: AxiosError) => {
      setFixingCameras(false);
      const errorMessage = errorMessageFrom(reason);
      if (errorMessage) {
        setFixResult({
          ...EMPTY_FIX_RESULT,
          open: true,
          error: true,
          errorMessage,
        });
        return;
      }
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        setFixResult({ ...EMPTY_FIX_RESULT, open: true, error: true });
      });
    }
  );
};
